import { useMemo, useState } from "react";
import { IconPlus, IconX } from "@tabler/icons-react";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { Input, Text } from "@/components/ui";
import { listarProveedoresAdmin } from "@/features/admin-proveedores/services/proveedores-service";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";

/** CSV de números de proveedor («109003,200») ↔ lista de números. */
function aNumeros(csv: string): number[] {
  return csv
    .split(",")
    .map((v) => Number(v.trim()))
    .filter((n) => Number.isInteger(n) && n > 0);
}

/**
 * Selector MÚLTIPLE de proveedores del catálogo para un ajuste de tipo «ListaProveedores»
 * (p. ej. Invoice:ProveedoresConIvaTrasladado). El valor es CSV de números de proveedor;
 * los elegidos se muestran como chips (número · razón social) y se agregan buscando por
 * nombre, RFC o número.
 */
export function ProveedoresConfigPicker({ value, onChange }: { value: string; onChange: (csv: string) => void }) {
  const seleccionados = useMemo(() => aNumeros(value), [value]);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDebounced] = useDebouncedValue(busqueda, 300);

  // Razón social de cada elegido · se busca por su número (el catálogo puede pasar de una página).
  const nombres = useQueries({
    queries: seleccionados.map((n) => ({
      queryKey: ["config-proveedor-nombre", n],
      queryFn: () => listarProveedoresAdmin({ busqueda: String(n), page: 1, pageSize: 20 }),
      staleTime: 60_000,
      select: (pagina: Awaited<ReturnType<typeof listarProveedoresAdmin>>) =>
        pagina.items.find((p) => p.numeroProveedor === n)?.razonSocial,
    })),
  });
  const { data: resultados, isFetching } = useQuery({
    queryKey: ["config-proveedores-busqueda", busquedaDebounced],
    queryFn: () => listarProveedoresAdmin({ busqueda: busquedaDebounced, soloActivos: true, page: 1, pageSize: 8 }),
    enabled: busquedaDebounced.trim().length > 0,
    placeholderData: keepPreviousData,
  });

  const nombre = (n: number) => nombres[seleccionados.indexOf(n)]?.data;
  const quitar = (n: number) => onChange(seleccionados.filter((s) => s !== n).join(","));
  const agregar = (n: number) => {
    if (!seleccionados.includes(n)) onChange([...seleccionados, n].join(","));
    setBusqueda("");
  };

  return (
    <div className="flex min-w-72 flex-col gap-1.5">
      {seleccionados.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {seleccionados.map((n) => (
            <span
              key={n}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-input bg-muted/40 py-0.5 pl-2.5 pr-1 text-xs"
            >
              <span className="font-mono">{n}</span>
              {nombre(n) && <span className="truncate text-muted-foreground">· {nombre(n)}</span>}
              <button
                type="button"
                onClick={() => quitar(n)}
                aria-label={`Quitar proveedor ${n}`}
                className="rounded-full p-0.5 text-muted-foreground hover:text-peligro-600"
              >
                <IconX size={12} stroke={2} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <Text size="xs" c="dimmed">
          Ningún proveedor seleccionado.
        </Text>
      )}
      <div className="relative">
        <Input
          size="xs"
          value={busqueda}
          onChange={(e) => setBusqueda(e.currentTarget.value)}
          placeholder="Agregar proveedor · p. ej. BLOOMBERG"
          aria-label="Buscar proveedor para agregar"
        />
        {busqueda.trim() && (
          <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
            {(resultados?.items ?? []).length === 0 ? (
              <Text size="xs" c="dimmed" className="px-2 py-1.5">
                {isFetching ? "Buscando…" : "Sin resultados."}
              </Text>
            ) : (
              resultados!.items.map((p) => {
                const ya = seleccionados.includes(p.numeroProveedor);
                return (
                  <button
                    key={p.numeroProveedor}
                    type="button"
                    disabled={ya}
                    onClick={() => agregar(p.numeroProveedor)}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-50"
                  >
                    <IconPlus size={12} className="shrink-0 text-muted-foreground" />
                    <span className="font-mono">{p.numeroProveedor}</span>
                    <span className="truncate">{p.razonSocial}</span>
                    <span className="ml-auto shrink-0 font-mono text-muted-foreground">{p.rfc}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
